import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/SupabaseAuthContext'
import { supabase } from '../config/supabase'
import { Download, ShoppingBag, Info, ChevronRight, Star, Monitor, Zap, Layers, Layout, X } from 'lucide-react'
import FloatingSidebar from '../components/FloatingSidebar'

import launcherCover from '../assets/images/launcher-cover.png'
import AsaasCheckout from '../components/payment/AsaasCheckout'

// Interfaces
interface SoftwarePlan {
  id?: string
  plan_key: string
  name?: string
  description?: string
  amount: number
  frequency_type?: string
  status?: string
  features?: string[]
  tokens?: number | null
  software_id?: string | null
  metadata?: any
}

interface Software {
  id: string
  name: string
  developer: string
  description: string
  status: 'available' | 'development' | 'early_access'
  priceType: 'free' | 'paid'
  priceDisplay?: string
  billingPeriod?: 'mensal' | 'anual' | 'vitalício'
  imageUrl: string
  // logoUrl removido para evitar duplicação visual
  downloadUrl?: string
  route: string
  mode?: string
  platforms?: string[] | null
  rating?: number
  tags?: string[]
  planKey?: string // Chave do plano para checkout direto
  plans?: SoftwarePlan[]
}

const StoreCard = ({ software, onPurchase }: { software: Software; onPurchase: (s: Software) => void }) => {
  const navigate = useNavigate()

  return (
    <motion.div
      className="bg-[#202020] rounded-xl overflow-hidden hover:bg-[#2a2a2a] transition-all duration-300 cursor-pointer group flex flex-col h-full border border-transparent hover:border-purple-500/30 hover:shadow-[0_0_30px_rgba(168,85,247,0.2)]"
      whileHover={{ y: -5 }}
      onClick={() => {
        if (software.status === 'available' && software.priceType === 'paid') {
          onPurchase(software)
        } else if (software.status === 'available' && software.priceType === 'free' && software.downloadUrl) {
          window.open(software.downloadUrl, '_blank')
        } else {
          navigate(software.route)
        }
      }}
    >
      {/* Image Area - Holographic 3D Effect */}
      <div className="relative aspect-[3/4] overflow-hidden bg-[#151515] group-hover:bg-[#1a1a1a] transition-colors">
        {/* Tech Grid Background */}
        <div
          className="absolute inset-0 opacity-20 transition-opacity duration-500 group-hover:opacity-30"
          style={{
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            backgroundPosition: 'center'
          }}
        />

        {/* Hologram Light Beam (Base) */}
        <div className="absolute bottom-[-20%] left-1/2 -translate-x-1/2 w-[120%] h-[40%] bg-purple-600/20 blur-[40px] rounded-full transition-all duration-500 group-hover:bg-purple-500/30 group-hover:blur-[50px]" />

        {/* Floating Logo Container */}
        <div className="absolute inset-0 flex items-center justify-center p-8 perspective-[1000px]">
          <motion.img
            src={software.imageUrl}
            alt={software.name}
            className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(168,85,247,0.3)] filter brightness-90 contrast-125"
            animate={{
              y: [0, -8, 0],
              filter: [
                'drop-shadow(0 0 10px rgba(168,85,247,0.3)) brightness(0.9)',
                'drop-shadow(0 0 20px rgba(168,85,247,0.6)) brightness(1.1)',
                'drop-shadow(0 0 10px rgba(168,85,247,0.3)) brightness(0.9)'
              ]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2 // Desync animations
            }}
            whileHover={{
              scale: 1.15,
              rotateX: 5,
              rotateY: 5,
              filter: 'drop-shadow(0 0 30px rgba(168,85,247,0.8)) brightness(1.2)'
            }}
          />
        </div>

        {/* Hologram Scanlines Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_4px,3px_100%] pointer-events-none opacity-20 mix-blend-overlay" />

        {/* Overlay Gradient Top */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#202020]/80 via-transparent to-transparent opacity-60 z-10 pointer-events-none" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-20">
          {software.priceType === 'free' && (
            <span className="bg-green-600/90 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded shadow-lg border border-green-400/30">
              GRÁTIS
            </span>
          )}
          {software.status === 'development' && (
            <span className="bg-yellow-600/90 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded shadow-lg border border-yellow-400/30">
              EM BREVE
            </span>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-auto">
          <div className="flex justify-between items-start mb-1">
            <h3 className="text-white font-bold text-lg leading-tight group-hover:text-purple-400 transition-colors">
              {software.name}
            </h3>
            {software.rating && (
              <div className="flex items-center gap-1 text-yellow-500 text-xs font-bold bg-yellow-500/10 px-1.5 py-0.5 rounded">
                <Star className="w-3 h-3 fill-current" />
                {software.rating}
              </div>
            )}
          </div>

          <p className="text-gray-400 text-sm mb-3 line-clamp-2">
            {software.description}
          </p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {software.tags?.slice(0, 3).map(tag => (
            <span key={tag} className="text-[10px] uppercase tracking-wider text-gray-500 bg-white/5 px-2 py-1 rounded">
              {tag}
            </span>
          ))}
        </div>

        {/* Footer / Price */}
        <div className="pt-3 border-t border-white/10 flex items-center justify-between mt-auto">
          <div className="flex flex-col">
            <span className="text-gray-400 text-xs">
              {software.billingPeriod ?
                (software.billingPeriod === 'anual' ? 'Anual' : 'Mensal')
                : 'Licença'}
            </span>
            <span className="text-white font-bold">
              {software.priceDisplay}
            </span>
          </div>

          <div className="flex gap-2">
            {software.route && (
              <button
                className="bg-white/10 text-white p-2 rounded-full hover:bg-white hover:text-black transition-colors z-20"
                title="Saiba Mais"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(software.route)
                }}
              >
                <Info className="w-5 h-5" />
              </button>
            )}

            {software.status === 'available' && software.priceType === 'free' && (
              <button
                className="bg-white/10 text-white p-2 rounded-full hover:bg-white hover:text-black transition-colors z-20"
                title="Baixar"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(software.downloadUrl, '_blank')
                }}
              >
                <Download className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function SoftwarePage() {
  const { isAuthenticated } = useAuth()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showProductModal, setShowProductModal] = useState(false)
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | undefined>(undefined)
  const [productPlanKey, setProductPlanKey] = useState<string | null>(null)
  const [selectedSoftware, setSelectedSoftware] = useState<Software | null>(null)
  const [softwareList, setSoftwareList] = useState<Software[]>([])
  const [selectedMode, setSelectedMode] = useState<string | null>(null)

  // Check if opened from launcher
  const isFromLauncher = new URLSearchParams(window.location.search).get('from') === 'launcher'

  // Filter out launcher for the grid if from launcher OR if displayed in Hero
  // Se for da Web (!isFromLauncher), o Launcher aparece no Hero, então removemos do grid para não duplicar (ou mantemos se o design pedir, mas o padrão anterior era remover).
  // Se for do Launcher (isFromLauncher), o Launcher deve ser oculto totalmente (sem Hero e sem Grid).
  const gridSoftwares = softwareList.filter(s => {
    if (s.id === 'launcher') {
      // Se for do Launcher, esconde sempre.
      if (isFromLauncher) return false;
      // Se for Web, esconde do grid pois já está no Hero.
      return false;
    }
    return true;
  })

  const formatDropboxUrl = (url: string) => {
    if (url.includes('dropbox.com')) {
      if (url.includes('dl=0')) return url.replace('dl=0', 'dl=1')
      if (!url.includes('dl=')) return url + (url.includes('?') ? '&dl=1' : '?dl=1')
    }
    return url
  }

  useEffect(() => {
    const fetchSoftwares = async () => {
      try {
        const { data: dbSoftwares, error } = await supabase
          .from('software_versions')
          .select(`
            *,
            subscription_plans (*)
          `)
          .eq('is_active', true)

        if (error) throw error

        const normalizedList: Software[] = (dbSoftwares || []).map((dbSw: any) => {
          const activePlans: SoftwarePlan[] = (dbSw.subscription_plans || [])
            .filter((p: any) => p?.status === 'active')
            .map((p: any) => ({
              id: p.id,
              plan_key: p.plan_key,
              name: p.name,
              description: p.description,
              amount: Number(p.amount ?? 0),
              frequency_type: p.frequency_type,
              status: p.status,
              features: Array.isArray(p.features) ? p.features : undefined,
              tokens: typeof p.tokens === 'number' ? p.tokens : (p.tokens ? Number(p.tokens) : null),
              software_id: p.software_id ?? null,
              metadata: p.metadata
            }))
            .filter((p: SoftwarePlan) => !!p.plan_key)

          const sortedActivePlans = [...activePlans].sort((a, b) => a.amount - b.amount)
          const primaryPlan = sortedActivePlans[0]

          let priceDisplay = 'Gratuito'
          let priceType: 'free' | 'paid' = 'free'
          let billingPeriod: 'mensal' | 'anual' | 'vitalício' | undefined = undefined

          if (primaryPlan && primaryPlan.amount > 0) {
            priceType = 'paid'
            priceDisplay = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(primaryPlan.amount)
            billingPeriod = primaryPlan.frequency_type === 'month' ? 'mensal' :
              primaryPlan.frequency_type === 'year' ? 'anual' : undefined
          }

          return {
            id: dbSw.id,
            name: dbSw.name,
            developer: 'Overpixel',
            description: dbSw.description || 'Software Overpixel',
            status: 'available',
            priceType,
            priceDisplay,
            billingPeriod,
            imageUrl: dbSw.image_url || launcherCover,
            downloadUrl: dbSw.download_url,
            route: (dbSw.id === 'spot-cloud')
              ? '/software/spot-cloud'
              : (dbSw.mode === 'web' ? (dbSw.download_url || '#') : `/software/${dbSw.id}`),
            mode: dbSw.mode,
            platforms: Array.isArray(dbSw.platforms) ? dbSw.platforms : null,
            rating: 5.0,
            tags: ['Novo', dbSw.category || 'App'],
            planKey: primaryPlan?.plan_key,
            plans: sortedActivePlans
          } as Software
        })

        setSoftwareList(normalizedList)

      } catch (err) {
        console.error('Failed to fetch softwares:', err)
      }
    }

    fetchSoftwares()
  }, [])

  const handlePurchase = (software: Software) => {
    if (software.planKey) {
      const siblings = softwareList.filter(s => s.name?.trim().toLowerCase() === software.name?.trim().toLowerCase())
      const hasWeb = siblings.some(s => (s.mode || '').toLowerCase() === 'web')
      const hasDesktop = siblings.some(s => (s.mode || '').toLowerCase() !== 'web')

      setSelectedSoftware(software)
      setProductPlanKey(software.planKey || software.plans?.[0]?.plan_key || null)
      setSelectedMode(
        software.mode
          ? software.mode
          : hasWeb
            ? 'web'
            : hasDesktop
              ? 'desktop'
              : null
      )
      setShowProductModal(true)
    } else {
      window.location.href = software.route
    }
  }

  const variantsForSelected = selectedSoftware
    ? softwareList.filter(s => s.name?.trim().toLowerCase() === selectedSoftware.name?.trim().toLowerCase())
    : []

  const activeVariant = (() => {
    if (!selectedSoftware) return null
    if (!selectedMode) return selectedSoftware
    const match = variantsForSelected.find(v => (v.mode || '').toLowerCase() === selectedMode.toLowerCase())
    return match || selectedSoftware
  })()

  const activeVariantId = activeVariant?.id || null
  const activeVariantPlans = activeVariant?.plans || []
  const productPlans = [...activeVariantPlans].sort((a, b) => (a.amount || 0) - (b.amount || 0))
  const selectedProductPlan = productPlans.find(p => p.plan_key === productPlanKey) || productPlans[0] || null

  useEffect(() => {
    if (!showProductModal) return
    if (!activeVariant) return
    const defaultKey = activeVariant.planKey || activeVariant.plans?.[0]?.plan_key || null
    setProductPlanKey(prev => prev || defaultKey)
  }, [showProductModal, activeVariantId])

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount || 0)

  const resolvePeriodLabel = (frequencyType?: string) => {
    const key = (frequencyType || '').toLowerCase()
    if (key.includes('month')) return 'Mensal'
    if (key.includes('year')) return 'Anual'
    return 'Recorrente'
  }

  const resolvePlatformsLabel = (variant: Software | null) => {
    if (!variant) return null

    const rawPlatforms = Array.isArray(variant.platforms) ? variant.platforms : null
    const normalizedPlatforms = rawPlatforms
      ? rawPlatforms
          .map(p => String(p).trim().toLowerCase())
          .filter(Boolean)
      : []

    if (normalizedPlatforms.length > 0) {
      const hasWeb = normalizedPlatforms.includes('web')
      const hasDesktop = normalizedPlatforms.some(p => p === 'desktop' || p === 'windows' || p === 'pc')
      if (hasWeb && hasDesktop) return 'Web e Desktop'
      if (hasWeb) return 'Web'
      if (hasDesktop) return 'Desktop'
      return rawPlatforms?.join(' e ') || null
    }

    const siblings = selectedSoftware
      ? softwareList.filter(s => s.name?.trim().toLowerCase() === selectedSoftware.name?.trim().toLowerCase())
      : []

    const hasWeb = siblings.some(s => (s.mode || '').toLowerCase() === 'web')
    const hasDesktop = siblings.some(s => (s.mode || '').toLowerCase() !== 'web')
    if (hasWeb && hasDesktop) return 'Web e Desktop'
    if ((variant.mode || '').toLowerCase() === 'web') return 'Web'
    if (variant.mode) return 'Desktop'
    if (hasWeb) return 'Web'
    if (hasDesktop) return 'Desktop'
    return null
  }

  const formatTokens = (tokens?: number | null) => {
    if (!tokens || Number.isNaN(tokens)) return null
    const formatted = Number(tokens).toLocaleString('pt-BR')
    return `${formatted} TKS`
  }

  const formatFeatureLabel = (raw: string) => {
    const value = String(raw || '').trim()
    if (!value) return null

    const lower = value.toLowerCase()

    const directMap: Record<string, string> = {
      'transparent_png': 'Exporta PNG com transparência',
      'transparent-png': 'Exporta PNG com transparência',
      'commercial_license': 'Licença comercial',
      'commercial-license': 'Licença comercial',
      'bulk_generation': 'Geração em lote',
      'bulk-generation': 'Geração em lote',
      'priority_support': 'Suporte prioritário',
      'priority-support': 'Suporte prioritário',
      '300dpi': 'Exportação em 300 DPI',
      '600dpi': 'Exportação em 600 DPI'
    }
    if (directMap[lower]) return directMap[lower]

    const tokensMatch = lower.match(/^(\d+)\s*[_-]?\s*tokens$/)
    if (tokensMatch?.[1]) {
      return `${Number(tokensMatch[1]).toLocaleString('pt-BR')} tokens/mês`
    }

    const cleaned = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    if (!cleaned) return null
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
  }

  return (
    <div className="min-h-screen bg-[#121212] text-white font-sans">
      {!showProductModal && !showCheckoutModal && !showLoginModal && <FloatingSidebar />}

      {/* Hero Section */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Section Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Layout className="w-6 h-6 text-purple-500" />
              Store
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Explore, assine e baixe os melhores softwares da Overpixel
            </p>
          </div>

          {/* Filters (Visual Only for now) */}
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm transition-colors text-white font-medium">
              Todos
            </button>
            <button className="px-4 py-2 hover:bg-white/5 rounded-full text-sm transition-colors text-gray-400">
              Apps
            </button>
            <button className="px-4 py-2 hover:bg-white/5 rounded-full text-sm transition-colors text-gray-400">
              Assinaturas
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {gridSoftwares.map((software) => (
            <StoreCard
              key={software.id}
              software={software}
              onPurchase={handlePurchase}
            />
          ))}

          {/* Card de "Em Breve" para preencher grid se necessário */}
          <div className="bg-[#1a1a1a] rounded-xl border border-white/5 flex flex-col items-center justify-center p-8 text-center min-h-[400px] opacity-50 hover:opacity-100 transition-opacity">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-300 mb-2">Mais em breve</h3>
            <p className="text-gray-500 text-sm">
              Novas ferramentas estão sendo desenvolvidas nos laboratórios Overpixel.
            </p>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowLoginModal(false)}
          >
            <div className="bg-[#202020] p-8 rounded-2xl max-w-md w-full border border-white/10" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-white mb-4 text-center">Autenticação Necessária</h3>
              <p className="text-gray-400 text-center mb-6">
                Faça login para baixar os softwares OverPixel gratuitamente.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/auth/login"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-bold text-center transition-colors"
                >
                  Entrar
                </Link>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProductModal && activeVariant && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-gradient-to-b from-black/60 via-black/70 to-black/80 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              className="relative w-full max-w-5xl h-[calc(100vh-1.5rem)] max-h-[780px] rounded-3xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_30px_120px_rgba(0,0,0,0.65)] overflow-hidden"
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(168,85,247,0.35),transparent_45%),radial-gradient(circle_at_82%_28%,rgba(34,211,238,0.18),transparent_45%),radial-gradient(circle_at_50%_110%,rgba(168,85,247,0.22),transparent_55%)]" />
              <div className="pointer-events-none absolute inset-0 opacity-60 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.06),transparent_30%,transparent_70%,rgba(0,0,0,0.35))]" />

              <div className="relative z-10 h-full flex flex-col min-h-0">
                <div className="p-4 md:p-6 border-b border-white/10 bg-white/5 backdrop-blur-2xl">
                  <div className="flex items-start gap-3">
                    <div className="hidden sm:flex w-14 h-14 rounded-2xl overflow-hidden border border-white/10 bg-white/5 items-center justify-center">
                      <img src={activeVariant.imageUrl} alt={activeVariant.name} className="w-full h-full object-contain p-2" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-gray-300">Software OverPixel</div>
                      <div className="text-xl md:text-2xl font-black text-white leading-tight truncate">
                        {activeVariant.name}
                      </div>
                      <div className="text-sm text-gray-200/85 mt-1 line-clamp-2">
                        {activeVariant.description}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowProductModal(false)}
                      className="bg-white/5 hover:bg-white/10 text-white p-2 rounded-full transition-colors backdrop-blur-xl border border-white/10"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mt-4 flex flex-col gap-3">
                    {variantsForSelected.length > 1 && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          className={`px-3 py-3 rounded-xl border transition-colors flex items-center justify-center gap-2 text-sm font-black ${
                            (selectedMode || '').toLowerCase() === 'web'
                              ? 'bg-purple-500/20 border-purple-400/30 text-white'
                              : 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10'
                          }`}
                          onClick={() => setSelectedMode('web')}
                          disabled={!variantsForSelected.some(v => (v.mode || '').toLowerCase() === 'web')}
                        >
                          <Layers className="w-4 h-4" />
                          Web
                        </button>
                        <button
                          className={`px-3 py-3 rounded-xl border transition-colors flex items-center justify-center gap-2 text-sm font-black ${
                            (selectedMode || '').toLowerCase() !== 'web'
                              ? 'bg-purple-500/20 border-purple-400/30 text-white'
                              : 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10'
                          }`}
                          onClick={() => setSelectedMode('desktop')}
                          disabled={!variantsForSelected.some(v => (v.mode || '').toLowerCase() !== 'web')}
                        >
                          <Monitor className="w-4 h-4" />
                          Desktop
                        </button>
                      </div>
                    )}

                    {(productPlans || []).length > 0 && (
                      <div className={`grid gap-2 ${productPlans.length === 1 ? 'grid-cols-1' : productPlans.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {productPlans.slice(0, 3).map(plan => {
                          const isSelected = plan.plan_key === (selectedProductPlan?.plan_key || '')
                          return (
                            <button
                              key={plan.plan_key}
                              className={`relative text-left rounded-2xl border p-3 backdrop-blur-xl transition-all overflow-hidden ${
                                isSelected
                                  ? 'bg-purple-500/12 border-purple-300/50 shadow-[0_0_0_1px_rgba(168,85,247,0.45),0_20px_60px_rgba(168,85,247,0.18)]'
                                  : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                              }`}
                              onClick={() => setProductPlanKey(plan.plan_key)}
                            >
                              {isSelected && (
                                <>
                                  <div className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-r from-purple-400/60 via-fuchsia-400/45 to-cyan-300/35 opacity-90" />
                                  <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-purple-500/20 blur-2xl" />
                                </>
                              )}

                              <div className="relative z-10 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-black text-white leading-tight line-clamp-2">
                                    {plan.name || 'Plano'}
                                  </div>
                                  <div className="text-[11px] text-gray-300 mt-0.5">
                                    {resolvePeriodLabel(plan.frequency_type)}
                                  </div>
                                  {isSelected && (
                                    <div className="mt-2">
                                      <span className="inline-flex items-center text-[10px] font-black tracking-wider uppercase text-white bg-black/25 border border-white/15 rounded-full px-2.5 py-1 backdrop-blur-xl">
                                        Selecionado
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="text-sm font-black text-white">
                                    {formatPrice(plan.amount)}
                                  </div>
                                  {formatTokens(plan.tokens) && (
                                    <div className="text-[11px] text-gray-300">
                                      {formatTokens(plan.tokens)} / mês
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
                      <div className="flex flex-wrap gap-2">
                        {resolvePlatformsLabel(activeVariant) && (
                          <span className="text-[11px] text-gray-100 bg-black/20 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur-xl">
                            Funciona em: {resolvePlatformsLabel(activeVariant)}
                          </span>
                        )}
                        {selectedProductPlan?.tokens && (
                          <span className="text-[11px] text-gray-100 bg-black/20 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur-xl">
                            {formatTokens(selectedProductPlan.tokens)} / mês
                          </span>
                        )}
                      </div>
                      <button
                        className="h-12 md:w-[260px] bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-600 hover:brightness-110 text-white rounded-2xl font-black transition-all shadow-[0_10px_30px_rgba(168,85,247,0.25)]"
                        onClick={() => {
                          if (!selectedProductPlan?.plan_key) return
                          if (!isAuthenticated) {
                            setShowLoginModal(true)
                            return
                          }
                          setSelectedPlanKey(selectedProductPlan.plan_key)
                          setShowProductModal(false)
                          setShowCheckoutModal(true)
                        }}
                      >
                        Assinar agora
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 md:p-6 flex-1 min-h-0">
                  <div className="h-full grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-4">
                    <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl p-4 md:p-5">
                      <div className="text-sm font-black text-white mb-3">O que você libera</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-200/90">
                        {(selectedProductPlan?.features || [])
                          .map(f => formatFeatureLabel(String(f)))
                          .filter((f): f is string => Boolean(f))
                          .slice(0, 10)
                          .map((feature, idx) => (
                            <div key={`${selectedProductPlan?.plan_key || 'plan'}-benefit-${idx}`} className="flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 text-purple-400 mt-[2px] shrink-0" />
                              <span className="line-clamp-2">{feature}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl p-4 md:p-5 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-black text-white leading-tight line-clamp-2">
                            {selectedProductPlan?.name || 'Plano'}
                          </div>
                          <div className="text-xs text-gray-300 mt-1">
                            {resolvePeriodLabel(selectedProductPlan?.frequency_type)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black text-white leading-none">
                            {formatPrice(Number(selectedProductPlan?.amount || 0))}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {resolvePeriodLabel(selectedProductPlan?.frequency_type) === 'Mensal' ? 'por mês' : resolvePeriodLabel(selectedProductPlan?.frequency_type) === 'Anual' ? 'por ano' : ''}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="rounded-2xl bg-black/20 border border-white/10 backdrop-blur-xl p-3">
                          <div className="text-[10px] text-gray-300 uppercase tracking-wider font-black">Libera</div>
                          <div className="text-sm font-black text-white mt-1 line-clamp-1">{activeVariant.name}</div>
                        </div>
                        <div className="rounded-2xl bg-black/20 border border-white/10 backdrop-blur-xl p-3">
                          <div className="text-[10px] text-gray-300 uppercase tracking-wider font-black">Tokens</div>
                          <div className="text-sm font-black text-white mt-1 line-clamp-1">
                            {formatTokens(selectedProductPlan?.tokens ?? null) || '—'}
                          </div>
                        </div>
                        <div className="rounded-2xl bg-black/20 border border-white/10 backdrop-blur-xl p-3 col-span-2">
                          <div className="text-[10px] text-gray-300 uppercase tracking-wider font-black">Funciona Em</div>
                          <div className="text-sm font-black text-white mt-1 line-clamp-1">
                            {resolvePlatformsLabel(activeVariant) || '—'}
                          </div>
                        </div>
                      </div>

                      {selectedProductPlan?.description && (
                        <div className="mt-4 text-sm text-gray-200/90 leading-relaxed line-clamp-3">
                          {selectedProductPlan.description}
                        </div>
                      )}

                      <div className="mt-auto pt-4">
                        <div className="text-[11px] text-gray-300 flex items-center justify-between gap-3">
                          <span>Pagamento seguro</span>
                          <span>Cancelamento quando quiser</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckoutModal && selectedPlanKey && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCheckoutModal(false)}
          >
            <div
              className="relative bg-[#181818] w-full max-w-4xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden my-8"
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="max-h-[90vh] overflow-y-auto custom-scrollbar">
                <div className="p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-white mb-2 text-center">Finalizar Assinatura</h2>
                  <p className="text-gray-400 text-center mb-8">Preencha seus dados para liberar o acesso imediato.</p>

                  {/* Wrapper para ajustar o tema claro/escuro do componente AsaasCheckout se necessário */}
                  <div className="checkout-wrapper">
                    <AsaasCheckout planKey={selectedPlanKey} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
